import { useEffect, useCallback, useRef } from 'react';
import useAppContext from './useAppContext';
import { AppContextType } from '../context/AppContext';

export const useUserData = () => {
  const { 
    userName, 
    userFirstname,
    profilePicture,
    setUserName, 
    setUserFirstname,
    setProfilePicture
  } = useAppContext() as AppContextType;

  const isInitialized = useRef(false);

  const loadUserDataFromStorage = useCallback(() => {
    try {
      const storedUserName = localStorage.getItem('userName');
      const storedUserFirstname = localStorage.getItem('userFirstname');
      const storedProfilePicture = localStorage.getItem('profilePicture');

      if (storedUserName !== null) setUserName(storedUserName);
      if (storedUserFirstname !== null) setUserFirstname(storedUserFirstname);
      if (storedProfilePicture !== null) setProfilePicture(storedProfilePicture);

      isInitialized.current = true;
    } catch (error) {
      console.error('Erreur lors du chargement utilisateur:', error);
    }
  }, [setUserName, setUserFirstname, setProfilePicture]);

  const forceReload = useCallback(() => {
    loadUserDataFromStorage();
  }, [loadUserDataFromStorage]);

  useEffect(() => {
    if (!isInitialized.current) {
      loadUserDataFromStorage();
    }
  }, [loadUserDataFromStorage]);

  useEffect(() => {
    const checkStorageChanges = () => {
      const storedUserName = localStorage.getItem('userName');
      const storedUserFirstname = localStorage.getItem('userFirstname');
      const storedProfilePicture = localStorage.getItem('profilePicture');

      if (storedUserName !== userName) setUserName(storedUserName || '');
      if (storedUserFirstname !== userFirstname) setUserFirstname(storedUserFirstname || '');
      if (storedProfilePicture !== profilePicture) {
        setProfilePicture(storedProfilePicture || null);
      }
    };

    const timers: NodeJS.Timeout[] = [];
    for (let i = 0; i < 10; i++) {
      timers.push(setTimeout(checkStorageChanges, i * 500));
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userName') setUserName(e.newValue || '');
      if (e.key === 'userFirstname') setUserFirstname(e.newValue || '');
      if (e.key === 'profilePicture') setProfilePicture(e.newValue);
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [userName, userFirstname, profilePicture, setUserName, setUserFirstname, setProfilePicture]);

  return { 
    userName, 
    userFirstname,
    profilePicture,
    loadUserDataFromStorage,
    forceReload
  };
};
